/**
 * Lightweight per-user display cooldowns backed by localStorage timestamps.
 * Used to keep low-value popups (the events ad, the check-in suggestion
 * dialog) from re-appearing on every app open / refresh.
 *
 * Keys are namespaced per user, e.g. `checkinSuggestionShownAt:42`.
 */

export const CHECKIN_SUGGESTION_COOLDOWN_KEY = 'checkinSuggestionShownAt';
export const EVENTS_AD_COOLDOWN_KEY = 'eventsAdShownAt';
export const PACKAGE_WARNING_DISMISSAL_KEY = 'packageWarningDismissedAt';

const storageKey = (key: string, userId: number | string): string => `${key}:${userId}`;

/** True if `markShown(key, userId)` was called within the last `minutes`. */
export const wasShownWithin = (key: string, userId: number | string, minutes: number): boolean => {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    const raw = window.localStorage.getItem(storageKey(key, userId));
    if (!raw) return false;
    const shownAt = Number(raw);
    if (!Number.isFinite(shownAt)) return false;
    return Date.now() - shownAt < minutes * 60 * 1000;
  } catch {
    return false;
  }
};

/** Record "shown now" for this key + user. */
export const markShown = (key: string, userId: number | string): void => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(storageKey(key, userId), String(Date.now()));
  } catch {
    // ignore storage failures (private mode, quota) — cooldown is best-effort
  }
};
